import React, {
  FC,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  Text,
  withSitecoreContext,
  RichText,
  isEditorActive,
  Link,
  ComponentConsumerProps
} from '@sitecore-jss/sitecore-jss-react'
import {
  AttachmentFields,
  AccordionListProps,
} from './types/AccordionList.types'
import { anchorWithId } from '../../helpers/anchorWithId.helper'
import './index.scss'

import { AttachmentLinkField } from '../../types/types'

const convertFileSize = (sizeInBytes) => {
  if (sizeInBytes) {
    const fielSize = (sizeInBytes / (1024 * 1024)).toFixed(2)
    const fielSizeNumber = parseInt(fielSize)
    if (fielSizeNumber < 1) {
      return (sizeInBytes / 1024).toFixed(2) + ' ' + ' KB'
    } else return (sizeInBytes / (1024 * 1024)).toFixed(2) + ' ' + 'MB'
  }
}

const AccordionList: FC<ComponentConsumerProps & AccordionListProps> = (props) => {
  const downloadListItems = props.fields?.Attachments;
  const downloadListTitle = props.fields?.Title;
  const loadMoreText = props.fields?.['Load More Text'];
  const numberOfDoc = parseInt(props.fields?.NumberOfAttachments?.value ?? '');

  const [currentDocToShow, setloadMore] = useState<number>(0);

  useEffect(() => {
    if (typeof window !== 'undefined' && 'customElements' in window) {
      import('@patternfly/pfe-icon-onhighway/dist/pfe-icon.js');
    }
  }, []);

  useEffect(() => {
    setloadMore(numberOfDoc);
  }, [])

  const ItemAccordion: FC<{ item: AttachmentFields }> = useMemo(() => {
    return function Component({ item }) {
      const [isOpen, setIsOpen] = useState(false);

      const handleAccordionClick = useCallback(() => {
        setIsOpen((prevValue) => !prevValue)
      }, [])

      const accordionRef = useRef<HTMLDivElement>(null);

      const LinkButton: FC<{ attachmentLinkField: AttachmentLinkField }> =
        useMemo(() => {
          return function Component({ attachmentLinkField }) {
            return (
              <div className="download__accordion__accordion_download">
                <div>
                  <div>{attachmentLinkField?.value?.displayName}</div>
                  {attachmentLinkField?.value?.src && (
                    <div className="file-info">
                      {isEditorActive() ? (
                        <Link field={attachmentLinkField} />
                      ) : (
                        <>
                          <Text
                            field={{
                              value: convertFileSize(
                                attachmentLinkField?.value?.size
                              ),
                            }}
                            tag="span"
                            className="file-size"
                          />
                          <p>
                            <Link
                              target={
                                item?.fields?.linktarget?.value &&
                                item?.fields?.linktarget?.value !== ''
                                  ? item?.fields?.linktarget?.value
                                  : '_blank'
                              }
                              field={{
                                value: attachmentLinkField?.value?.src,
                              }}
                              href={anchorWithId(
                                attachmentLinkField?.value?.src,
                                attachmentLinkField?.value?.anchor
                              )}
                            >
                              <Text
                                field={{
                                  value: attachmentLinkField?.value?.extension,
                                }}
                                tag="span"
                                className="file-extension"
                              />
                              <pfe-icon
                                size={24}
                                icon="cnh-download"
                                color="base"
                              />
                            </Link>
                          </p>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )
          }
        }, [])

      const Component: FC = useMemo(() => {
        return function Component() {
          return (
            <div
              className="download__accordion__media AccordionList__top_padding"
              id={props?.rendering?.uid}
            >
              <div
                className="download__accordion__header__overlay"
                onClick={handleAccordionClick}
              >
                <div
                  className={`download__accordion__header__overlay__arrow ${
                    isOpen
                      ? 'download__accordion__header__overlay__arrow--reverse'
                      : ''
                  }`}
                />
              </div>

              <div className="rte-event-list-right">
                <div className="rte-event-list-header-wrapper">
                  <div className="rte-event-list-header">
                    <RichText
                      field={item.fields?.Title}
                      className="download__accordion__accordion_title"
                    />

                    <p className="download__accordion__spacing">
                      <RichText
                        field={item.fields?.['Attachment Name']}
                        className="rte-event-list-heading rte-event-list-headingException"
                        tag="h3"
                      />
                    </p>

                    {item.fields?.['Attachment Link']?.value && (
                      <LinkButton
                        attachmentLinkField={item.fields?.['Attachment Link']}
                      />
                    )}

                    {item.fields?.['Attachment Link Second']?.value && (
                      <LinkButton
                        attachmentLinkField={
                          item.fields?.['Attachment Link Second']
                        }
                      />
                    )}

                    {item.fields?.['Attachment Link Third']?.value && (
                      <LinkButton
                        attachmentLinkField={
                          item.fields?.['Attachment Link Third']
                        }
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          )
        }
      }, [item, isOpen, handleAccordionClick])

      return (
        <div
          ref={accordionRef}
          style={{
            maxHeight: isOpen ? accordionRef?.current?.scrollHeight : 111,
            overflow: 'hidden',
            transition: 'max-height 0.5s',
          }}
        >
          <Component />
        </div>
      )
    }
  }, [])

  return (
    <>
      {props.fields && (
        <div className="rte-event" id={props?.rendering?.uid}>
          <Text
            field={downloadListTitle}
            className="rte-event-heading"
            tag="h2"
          />

          {(downloadListItems || [])
            .slice(0, currentDocToShow)
            .map((item, index: number) => (
              <div
                key={index}
                className={
                  index % 2
                    ? 'rte-event-list rte-event-listBKB'
                    : 'rte-event-list rte-event-listBKA'
                }
              >
                <ItemAccordion item={item} />
              </div>
            ))}

          {currentDocToShow < (downloadListItems?.length ?? 0) ? (
            <div
              className="load-more"
              onClick={() => setloadMore(currentDocToShow + numberOfDoc)}
            >
              {isEditorActive() ? (
                <Link field={loadMoreText as any} />
              ) : (
                <Link
                  field={{
                    value: '',
                  }}
                  href=""
                >
                  <Text
                    field={{ value: loadMoreText?.value }}
                    tag="span"
                    className="load-more-text"
                  />
                  <pfe-icon icon="cnh-arrow-forward" size={24}></pfe-icon>
                </Link>
              )}
            </div>
          ) : null}
        </div>
      )}
    </>
  )
}

export default withSitecoreContext()(memo(AccordionList))
